import { useEffect, useState } from "react";
import client from "../api/client";

const tabs = [
  ["journals", "Journal"],
  ["opening-balances", "Opening Balance"],
  ["purchase", "Purchase Voucher"],
  ["sales", "Sales Voucher"],
  ["credit-note", "Credit Note"],
  ["debit-note", "Debit Note"],
];
const g = (r, ...keys) =>
  keys.map((k) => r?.[k]).find((v) => v !== undefined && v !== null) ?? "";
const date = (v) => (v ? String(v).slice(0, 10) : "");
const companyId = () => Number(localStorage.getItem("companyId") || 0);

export default function EditManagerPage() {
  const [tab, setTab] = useState("journals");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const cid = companyId();
      const url =
        tab === "journals"
          ? `/edit-support/journals?companyId=${cid}`
          : tab === "opening-balances"
            ? `/edit-support/opening-balances?companyId=${cid}`
            : `/edit-support/vouchers/${tab}?companyId=${cid}`;
      const res = await client.get(url);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      alert(e.response?.data || "Unable to load records");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    setEditing(null);
  }, [tab]);

  const startEdit = (r) => {
    setEditing({
      id: g(r, "Id", "id"),
      voucherNo: g(r, "VoucherNo", "voucherNo"),
      voucherDate: date(
        g(r, "VoucherDate", "voucherDate", "EntryDate", "entryDate"),
      ),
      partyId: g(r, "PartyId", "partyId"),
      voucherMode: g(r, "VoucherMode", "voucherMode"),
      notes: g(
        r,
        "Notes",
        "notes",
        "Description",
        "description",
        "Remarks",
        "remarks",
      ),
      accountId: g(r, "AccountId", "accountId"),
      openingDate: date(g(r, "OpeningDate", "openingDate")),
      debit: Number(g(r, "Debit", "debit") || 0),
      credit: Number(g(r, "Credit", "credit") || 0),
    });
  };

  const saveEdit = async () => {
    if (!editing?.id) return;
    try {
      if (tab === "opening-balances") {
        await client.put(`/edit-support/opening-balances/${editing.id}`, {
          companyId: companyId(),
          accountId: Number(editing.accountId),
          entryDate: editing.openingDate,
          debit: Number(editing.debit || 0),
          credit: Number(editing.credit || 0),
          notes: editing.notes || "",
        });
      } else if (tab === "journals") {
        alert(
          "Journal line editing is available from Journal screen. This manager supports voucher/header and opening-balance updates.",
        );
        return;
      } else {
        await client.put(`/edit-support/vouchers/${tab}/${editing.id}`, {
          companyId: companyId(),
          voucherNo: editing.voucherNo,
          voucherDate: editing.voucherDate,
          partyId: Number(editing.partyId || 0),
          voucherMode: editing.voucherMode,
          notes: editing.notes || "",
        });
      }
      setEditing(null);
      await load();
      alert("Updated successfully");
    } catch (e) {
      alert(e.response?.data || "Unable to update");
    }
  };

  const remove = async (r) => {
    const id = g(r, "Id", "id");
    if (
      !id ||
      !window.confirm(
        "Delete this record? For vouchers, matching journal and stock rows with the same voucher no will also be removed.",
      )
    )
      return;
    const url =
      tab === "journals"
        ? `/edit-support/journals/${id}`
        : tab === "opening-balances"
          ? `/edit-support/opening-balances/${id}`
          : `/edit-support/vouchers/${tab}/${id}`;
    await client.delete(url);
    await load();
  };

  return (
    <div className="container-fluid py-2" style={{ fontSize: 11 }}>
      <div
        className="card border-0 shadow-sm"
        style={{ borderRadius: 14, overflow: "hidden" }}
      >
        <div
          className="card-header text-white py-2"
          style={{
            background: "linear-gradient(90deg, #22052D, #CCB3D1)",
          }}
        >
          <h6 className="mb-0" style={{ fontSize: 13, fontWeight: 700 }}>
            <i className="bi bi-pencil-square me-2" />
            Edit / Update / Delete Manager
          </h6>
          <small style={{ fontSize: 10, opacity: 0.85 }}>
            Company-wise corrections for opening balances and vouchers
          </small>
        </div>
        <div className="card-body p-2">
          <div className="d-flex flex-wrap gap-1 mb-2">
            {tabs.map(([k, label]) => (
              <button
                key={k}
                className={`btn btn-sm ${tab === k ? "btn-primary" : "btn-outline-primary"}`}
                style={{ fontSize: 11 }}
                onClick={() => setTab(k)}
              >
                {label}
              </button>
            ))}
          </div>

          {editing && (
            <div className="border rounded p-2 mb-2 bg-light">
              <div className="fw-bold mb-2">
                Edit {tabs.find((x) => x[0] === tab)?.[1]}
              </div>
              {tab === "opening-balances" ? (
                <div className="row g-2">
                  <div className="col-md-2">
                    <label>Account Id</label>
                    <input
                      className="form-control form-control-sm"
                      value={editing.accountId}
                      onChange={(e) =>
                        setEditing({ ...editing, accountId: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-2">
                    <label>Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={editing.openingDate}
                      onChange={(e) =>
                        setEditing({ ...editing, openingDate: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-2">
                    <label>Debit</label>
                    <input
                      className="form-control form-control-sm"
                      value={editing.debit}
                      onChange={(e) =>
                        setEditing({ ...editing, debit: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-2">
                    <label>Credit</label>
                    <input
                      className="form-control form-control-sm"
                      value={editing.credit}
                      onChange={(e) =>
                        setEditing({ ...editing, credit: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-4">
                    <label>Remarks</label>
                    <input
                      className="form-control form-control-sm"
                      value={editing.notes}
                      onChange={(e) =>
                        setEditing({ ...editing, notes: e.target.value })
                      }
                    />
                  </div>
                </div>
              ) : (
                <div className="row g-2">
                  <div className="col-md-2">
                    <label>Voucher No</label>
                    <input
                      className="form-control form-control-sm"
                      value={editing.voucherNo}
                      onChange={(e) =>
                        setEditing({ ...editing, voucherNo: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-2">
                    <label>Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={editing.voucherDate}
                      onChange={(e) =>
                        setEditing({ ...editing, voucherDate: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-2">
                    <label>Party Id</label>
                    <input
                      className="form-control form-control-sm"
                      value={editing.partyId}
                      onChange={(e) =>
                        setEditing({ ...editing, partyId: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-2">
                    <label>Mode</label>
                    <input
                      className="form-control form-control-sm"
                      value={editing.voucherMode}
                      onChange={(e) =>
                        setEditing({ ...editing, voucherMode: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-4">
                    <label>Notes</label>
                    <input
                      className="form-control form-control-sm"
                      value={editing.notes}
                      onChange={(e) =>
                        setEditing({ ...editing, notes: e.target.value })
                      }
                    />
                  </div>
                </div>
              )}
              <div className="mt-2 d-flex gap-2">
                <button
                  className="btn btn-success btn-sm"
                  style={{ fontSize: 11 }}
                  onClick={saveEdit}
                >
                  Update
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ fontSize: 11 }}
                  onClick={() => setEditing(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div>Loading...</div>
          ) : (
            <div
              style={{
                maxHeight: 460,
                overflow: "auto",
                border: "1px solid #e9ecef",
                borderRadius: 10,
              }}
            >
              <table
                className="table table-bordered table-hover mb-0 align-middle"
                style={{ fontSize: 11, minWidth: 900 }}
              >
                <thead
                  className="table-dark"
                  style={{ position: "sticky", top: 0 }}
                >
                  <tr>
                    {tab === "opening-balances"
                      ? [
                          "Id",
                          "Date",
                          "Account",
                          "Type",
                          "Debit",
                          "Credit",
                          "Remarks",
                          "Action",
                        ].map((h) => <th key={h}>{h}</th>)
                      : [
                          "Id",
                          "Voucher No",
                          "Date",
                          "Party/Account",
                          "Mode",
                          "Sub Total",
                          "Tax",
                          "Discount",
                          "Grand Total",
                          "Notes",
                          "Action",
                        ].map((h) => <th key={h}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="text-center text-muted py-3">
                        No records found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, i) =>
                      tab === "opening-balances" ? (
                        <tr key={i}>
                          <td>{g(r, "Id", "id")}</td>
                          <td>{date(g(r, "OpeningDate", "openingDate"))}</td>
                          <td>
                            {g(r, "AccountCode", "accountCode")} -{" "}
                            {g(r, "AccountName", "accountName")}
                          </td>
                          <td>{g(r, "AccountType", "accountType")}</td>
                          <td className="text-end">
                            {Number(g(r, "Debit", "debit") || 0).toFixed(2)}
                          </td>
                          <td className="text-end">
                            {Number(g(r, "Credit", "credit") || 0).toFixed(2)}
                          </td>
                          <td>{g(r, "Remarks", "remarks")}</td>
                          <td className="d-flex gap-1">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              style={{ fontSize: 10 }}
                              onClick={() => startEdit(r)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              style={{ fontSize: 10 }}
                              onClick={() => remove(r)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ) : (
                        <tr key={i}>
                          <td>{g(r, "Id", "id")}</td>
                          <td>
                            <strong>{g(r, "VoucherNo", "voucherNo")}</strong>
                          </td>
                          <td>
                            {date(
                              g(
                                r,
                                "VoucherDate",
                                "voucherDate",
                                "EntryDate",
                                "entryDate",
                              ),
                            )}
                          </td>
                          <td>
                            {g(
                              r,
                              "PartyId",
                              "partyId",
                              "Description",
                              "description",
                            )}
                          </td>
                          <td>{g(r, "VoucherMode", "voucherMode")}</td>
                          <td className="text-end">
                            {Number(g(r, "SubTotal", "subTotal") || 0).toFixed(
                              2,
                            )}
                          </td>
                          <td className="text-end">
                            {Number(
                              g(r, "TaxAmount", "taxAmount") || 0,
                            ).toFixed(2)}
                          </td>
                          <td className="text-end">
                            {Number(
                              g(r, "DiscountAmount", "discountAmount") || 0,
                            ).toFixed(2)}
                          </td>
                          <td className="text-end">
                            {Number(
                              g(r, "GrandTotal", "grandTotal") || 0,
                            ).toFixed(2)}
                          </td>
                          <td>{g(r, "Notes", "notes")}</td>
                          <td className="d-flex gap-1">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              style={{ fontSize: 10 }}
                              onClick={() => startEdit(r)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              style={{ fontSize: 10 }}
                              onClick={() => remove(r)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ),
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
